import z from "zod";
import { customIssue, ValidationCode } from "../errors";
import { timestampsSchema } from "../helpers";
import { labelSchema } from "../label";
export var EvalLogicNodeTypeEnum;
(function (EvalLogicNodeTypeEnum) {
    EvalLogicNodeTypeEnum["GROUP"] = "GROUP";
    EvalLogicNodeTypeEnum["LIMIT"] = "LIMIT";
    EvalLogicNodeTypeEnum["OPERATOR"] = "OPERATOR";
})(EvalLogicNodeTypeEnum || (EvalLogicNodeTypeEnum = {}));
export var EvalLogicNodeOperatorValueEnum;
(function (EvalLogicNodeOperatorValueEnum) {
    EvalLogicNodeOperatorValueEnum["AND"] = "AND";
    EvalLogicNodeOperatorValueEnum["OR"] = "OR";
    EvalLogicNodeOperatorValueEnum["NOT"] = "NOT";
})(EvalLogicNodeOperatorValueEnum || (EvalLogicNodeOperatorValueEnum = {}));
export const evalLogicNodeSchema = z.union([
    z.object({
        id: z.uuidv7(),
        type: z.literal(EvalLogicNodeTypeEnum.GROUP),
        get children() {
            return z.array(evalLogicNodeSchema);
        },
    }),
    z.object({
        id: z.uuidv7(),
        type: z.literal(EvalLogicNodeTypeEnum.LIMIT),
    }),
    z.object({
        id: z.uuidv7(),
        type: z.literal(EvalLogicNodeTypeEnum.OPERATOR),
        operatorValue: z.enum(EvalLogicNodeOperatorValueEnum),
    }),
]);
export var EvalTestCaseTypeEnum;
(function (EvalTestCaseTypeEnum) {
    EvalTestCaseTypeEnum["CHECK"] = "CHECK";
    EvalTestCaseTypeEnum["DEFECT"] = "DEFECT";
})(EvalTestCaseTypeEnum || (EvalTestCaseTypeEnum = {}));
export var EvalActionTypeEnum;
(function (EvalActionTypeEnum) {
    EvalActionTypeEnum["WARNING"] = "WARNING";
    EvalActionTypeEnum["ALERT"] = "ALERT";
    EvalActionTypeEnum["GPIO"] = "GPIO";
})(EvalActionTypeEnum || (EvalActionTypeEnum = {}));
export var EvalGpioStateEnum;
(function (EvalGpioStateEnum) {
    EvalGpioStateEnum["HIGH"] = "HIGH";
    EvalGpioStateEnum["LOW"] = "LOW";
    EvalGpioStateEnum["TOGGLE"] = "TOGGLE";
})(EvalGpioStateEnum || (EvalGpioStateEnum = {}));
export const evalGpioActionSchema = z.object({
    type: z.literal(EvalActionTypeEnum.GPIO),
    pin: z.union([z.literal(1), z.literal(2)]),
    state: z.enum(EvalGpioStateEnum),
    durationMs: z.number().int().min(1).max(60_000).nullable(),
});
export const evalActionSchema = z.discriminatedUnion("type", [
    z.object({ type: z.literal(EvalActionTypeEnum.ALERT) }),
    z.object({ type: z.literal(EvalActionTypeEnum.WARNING) }),
    evalGpioActionSchema,
]);
function validateActions(actions, ctx) {
    const seenTypes = new Set();
    const seenPins = new Set();
    actions.forEach((action, index) => {
        if (action.type !== EvalActionTypeEnum.GPIO && seenTypes.has(action.type)) {
            ctx.addIssue(customIssue(ValidationCode.EVAL_ACTION_DUPLICATE, `Duplicate ${action.type} action`, {
                path: [index, "type"],
                params: { type: action.type },
            }));
        }
        seenTypes.add(action.type);
        if (action.type === EvalActionTypeEnum.GPIO) {
            if (seenPins.has(action.pin)) {
                ctx.addIssue(customIssue(ValidationCode.EVAL_GPIO_PIN_DUPLICATE, `Pin ${action.pin} already has an action`, {
                    path: [index, "pin"],
                    params: { pin: action.pin },
                }));
            }
            seenPins.add(action.pin);
        }
    });
    if (seenTypes.has(EvalActionTypeEnum.ALERT) &&
        seenTypes.has(EvalActionTypeEnum.WARNING)) {
        ctx.addIssue(customIssue(ValidationCode.EVAL_ALERT_WITH_WARNING, "Alert and Warning cannot both be set", {
            path: [],
        }));
    }
}
export const evalActionsSchema = z
    .array(evalActionSchema)
    .superRefine(validateActions);
export var EvalLimitItemParameterEnum;
(function (EvalLimitItemParameterEnum) {
    EvalLimitItemParameterEnum["POSITION"] = "POSITION";
    EvalLimitItemParameterEnum["AREA"] = "AREA";
    EvalLimitItemParameterEnum["COUNT"] = "COUNT";
})(EvalLimitItemParameterEnum || (EvalLimitItemParameterEnum = {}));
export var EvalLimitItemEdgeEnum;
(function (EvalLimitItemEdgeEnum) {
    EvalLimitItemEdgeEnum["LEFT"] = "LEFT";
    EvalLimitItemEdgeEnum["RIGHT"] = "RIGHT";
    EvalLimitItemEdgeEnum["TOP"] = "TOP";
    EvalLimitItemEdgeEnum["BOTTOM"] = "BOTTOM";
    EvalLimitItemEdgeEnum["CENTER"] = "CENTER";
})(EvalLimitItemEdgeEnum || (EvalLimitItemEdgeEnum = {}));
export var EvalLimitItemOperatorEnum;
(function (EvalLimitItemOperatorEnum) {
    EvalLimitItemOperatorEnum["AND"] = "AND";
    EvalLimitItemOperatorEnum["OR"] = "OR";
})(EvalLimitItemOperatorEnum || (EvalLimitItemOperatorEnum = {}));
export var EvalLimitItemQuantifierTypeEnum;
(function (EvalLimitItemQuantifierTypeEnum) {
    EvalLimitItemQuantifierTypeEnum["MIN"] = "MIN";
    EvalLimitItemQuantifierTypeEnum["MAX"] = "MAX";
    EvalLimitItemQuantifierTypeEnum["EXACT"] = "EXACT";
})(EvalLimitItemQuantifierTypeEnum || (EvalLimitItemQuantifierTypeEnum = {}));
export var EvalLimitItemQuantifierUnitEnum;
(function (EvalLimitItemQuantifierUnitEnum) {
    EvalLimitItemQuantifierUnitEnum["PERCENT"] = "PERCENT";
    EvalLimitItemQuantifierUnitEnum["PCS"] = "PCS";
})(EvalLimitItemQuantifierUnitEnum || (EvalLimitItemQuantifierUnitEnum = {}));
function validateEdgePair(data, ctx) {
    if (data.parameter !== EvalLimitItemParameterEnum.POSITION)
        return;
    const horizontal = new Set([EvalLimitItemEdgeEnum.LEFT, EvalLimitItemEdgeEnum.RIGHT]);
    const vertical = new Set([EvalLimitItemEdgeEnum.TOP, EvalLimitItemEdgeEnum.BOTTOM]);
    const targetIsH = horizontal.has(data.targetEdge);
    const targetIsV = vertical.has(data.targetEdge);
    const parentIsH = horizontal.has(data.parentEdge);
    const parentIsV = vertical.has(data.parentEdge);
    if ((targetIsH && parentIsV) || (targetIsV && parentIsH)) {
        ctx.addIssue(customIssue(ValidationCode.EVAL_EDGE_AXIS_MISMATCH, "Target edge and parent edge must be on the same axis, or one must be CENTER", { path: ["parentEdge"] }));
    }
}
const evalLimitItemBaseSchema = z.object({
    id: z.uuidv7(),
    limitFrom: z.number().nullable(),
    limitTo: z.number().nullable(),
    parameter: z.enum(EvalLimitItemParameterEnum),
    operator: z.enum(EvalLimitItemOperatorEnum),
    quantifierType: z.enum(EvalLimitItemQuantifierTypeEnum),
    quantifierUnit: z.enum(EvalLimitItemQuantifierUnitEnum),
    quantifierValue: z.number(),
    targetEdge: z.enum(EvalLimitItemEdgeEnum),
    parentEdge: z.enum(EvalLimitItemEdgeEnum),
    createdAt: timestampsSchema.createdAt,
    updatedAt: timestampsSchema.updatedAt,
});
export const evalLimitItemSchema = evalLimitItemBaseSchema.superRefine(validateEdgePair);
export const evalLimitSchema = z.object({
    id: z.uuidv7(),
    name: z.string(),
    actions: evalActionsSchema,
    enabled: z.boolean(),
    targetLabel: labelSchema,
    targetParentLabel: labelSchema.nullable(),
    createdAt: timestampsSchema.createdAt,
    updatedAt: timestampsSchema.updatedAt,
});
export const evalLimitDetailSchema = evalLimitSchema.extend({
    limitItems: z.array(evalLimitItemSchema),
});
export const evalTestCaseSchema = z.object({
    id: z.uuidv7(),
    name: z.string(),
    type: z.enum(EvalTestCaseTypeEnum),
    actions: evalActionsSchema,
    enabled: z.boolean(),
    limits: z.array(evalLimitSchema),
    createdAt: timestampsSchema.createdAt,
    updatedAt: timestampsSchema.updatedAt,
});
export const evalTestCaseDetailSchema = evalTestCaseSchema.extend({
    logicNodes: evalLogicNodeSchema.array(),
});
export const evalTestCaseFullSchema = evalTestCaseDetailSchema.extend({
    limits: z.array(evalLimitDetailSchema),
});
export const evalThresholdSchema = z.object({
    id: z.string(),
    name: z.string(),
    color: z.string(),
    value: z.number(),
    createdAt: timestampsSchema.createdAt,
    updatedAt: timestampsSchema.updatedAt,
});
export const evalTestCaseThresholdSchema = evalTestCaseSchema
    .pick({
    id: true,
    name: true,
    createdAt: true,
    updatedAt: true,
})
    .extend({
    thresholds: evalThresholdSchema.array(),
});
export const evalTestCaseCreateOrUpdateSchema = evalTestCaseDetailSchema
    .pick({
    name: true,
    type: true,
    actions: true,
    enabled: true,
})
    .extend({
    logicNodes: evalLogicNodeSchema.array().optional(),
});
export const evalLimitCreateOrUpdateSchema = evalLimitSchema
    .pick({
    name: true,
    actions: true,
    enabled: true,
})
    .extend({
    targetLabelId: z.number(),
    targetParentLabelId: z.number().nullable(),
    limitItems: evalLimitItemBaseSchema
        .pick({
        limitFrom: true,
        limitTo: true,
        parameter: true,
        operator: true,
        quantifierType: true,
        quantifierUnit: true,
        quantifierValue: true,
        targetEdge: true,
        parentEdge: true,
    })
        .extend({
        id: z.uuidv7().nullable(),
    })
        .superRefine(validateEdgePair)
        .array(),
});
export const evalTestCaseFullCreateOrUpdateSchema = evalTestCaseDetailSchema.pick({
    name: true,
    type: true,
    actions: true,
    enabled: true,
}).extend({
    logicNodes: evalLogicNodeSchema.array().optional(),
    limits: evalLimitCreateOrUpdateSchema.extend({ id: z.uuidv7().nullish() }).array().optional()
});
export const evalThresholdCreateOrUpdateSchema = evalThresholdSchema.pick({
    name: true,
    color: true,
    value: true,
});
export const evalThresholdsResponseSchema = z.object({
    testCases: z.array(evalTestCaseThresholdSchema),
    master: z.array(evalThresholdSchema),
});
//# sourceMappingURL=eval.schema.js.map